import { useContext, useEffect, useMemo, useState } from "react";
import { NotesProvider } from "../../../../workflow/management/Sections/Notes/NotesProvider";
import WorkFlowManagementContext from "../../../../workflow/management/WorkFlowManagementProvider";
import DateTimeContainer from "./DateTimeContainer";
import WorkFlowHistoryEntryLineItem from "./WorkFlowHistoryEntryLineItem";
import WorkFlowPatientHistoryAddItemModal from "./WorkFlowPatientHistoryAddItemModal";

export default function WorkFlowPatientHistoryEntries({ scrollToDate } = {}) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const { history } = useContext(WorkFlowManagementContext);
  const [showModal, setShowModal] = useState(undefined);
  // Build a tokenized searchable text from an item. We'll precompute this when history is loaded
  // so we can reuse it when filtering and avoid recomputing large concatenations on every keystroke.
  const itemToSearchableText = (item) => {
    let parts = [];
    if (item.wfEvent && item.wfEvent.name) parts.push(item.wfEvent.name);
    if (item.triggeredDateTime) parts.push(item.triggeredDateTime);
    // add month name tokens for triggeredDateTime so 'Jul' / 'July' will match
    try {
      const dt = new Date(item.triggeredDateTime);
      if (!isNaN(dt)) {
        const monthLong = [
          "january",
          "february",
          "march",
          "april",
          "may",
          "june",
          "july",
          "august",
          "september",
          "october",
          "november",
          "december",
        ][dt.getMonth()];
        const monthShort = [
          "jan",
          "feb",
          "mar",
          "apr",
          "may",
          "jun",
          "jul",
          "aug",
          "sep",
          "oct",
          "nov",
          "dec",
        ][dt.getMonth()];
        parts.push(monthLong);
        parts.push(monthShort);
        parts.push(String(dt.getFullYear()));
      }
    } catch (e) {}
    if (item.id) parts.push(String(item.id));

    // notes - handle several shapes: a note entry may be a string, or an object with different fields
    if (item.notes) {
      for (const n of item.notes) {
        if (!n) continue;
        // if the note entry is a plain string
        if (typeof n === "string") {
          parts.push(n);
          continue;
        }

        // common wrapper where the content lives under `note` property
        const noteObj = n.note && typeof n.note !== "string" ? n.note : n;

        const pushIfStr = (v) => {
          if (v && typeof v === "string") parts.push(v);
        };

        // common fields that might hold the note text
        pushIfStr(noteObj.note);
        pushIfStr(noteObj.text);
        pushIfStr(noteObj.body);
        pushIfStr(noteObj.content);
        pushIfStr(noteObj.description);
        pushIfStr(noteObj.title);
        pushIfStr(noteObj.subject);

        // sometimes note wrapper contains metadata with user info
        if (n.createdUser) {
          pushIfStr(n.createdUser.firstName);
          pushIfStr(n.createdUser.lastName);
        }
        if (noteObj.createdUser) {
          pushIfStr(noteObj.createdUser.firstName);
          pushIfStr(noteObj.createdUser.lastName);
        }

        // fallback: try to stringify small text fields directly on the object
        try {
          for (const k of ["note", "text", "body", "content", "description"]) {
            if (k in n && typeof n[k] === "string") pushIfStr(n[k]);
          }
        } catch (e) {
          /* ignore */
        }
      }
    }
    // documents
    if (item.documents) {
      for (const d of item.documents) {
        const doc = d.document || d;
        if (doc && doc.title) parts.push(doc.title);
        if (doc && doc.name) parts.push(doc.name);
        if (doc && doc.description) parts.push(doc.description);
        if (doc && doc.createdUser) {
          if (doc.createdUser.firstName) parts.push(doc.createdUser.firstName);
          if (doc.createdUser.lastName) parts.push(doc.createdUser.lastName);
        }
      }
    }
    // messages
    if (item.messages) {
      for (const m of item.messages) {
        if (m.subject) parts.push(m.subject);
        if (m.body) parts.push(m.body);
        if (m.from) parts.push(m.from);
        if (m.to) parts.push(m.to);
        if (m.createdUser) {
          if (m.createdUser.firstName) parts.push(m.createdUser.firstName);
          if (m.createdUser.lastName) parts.push(m.createdUser.lastName);
        }
      }
    }
    // calendar entries and questionnaires
    if (item.calendarEntries) {
      for (const c of item.calendarEntries) {
        if (c.title) parts.push(c.title);
        if (c.description) parts.push(c.description);
        if (c.location) parts.push(c.location);
        if (c.createdUser) {
          if (c.createdUser.firstName) parts.push(c.createdUser.firstName);
          if (c.createdUser.lastName) parts.push(c.createdUser.lastName);
        }
        // include month tokens for calendar entry dates if present
        try {
          const calDate = c.startDate || c.date || c.from || c.to;
          if (calDate) {
            const dtc = new Date(calDate);
            if (!isNaN(dtc)) {
              const monthLongC = [
                "january",
                "february",
                "march",
                "april",
                "may",
                "june",
                "july",
                "august",
                "september",
                "october",
                "november",
                "december",
              ][dtc.getMonth()];
              const monthShortC = [
                "jan",
                "feb",
                "mar",
                "apr",
                "may",
                "jun",
                "jul",
                "aug",
                "sep",
                "oct",
                "nov",
                "dec",
              ][dtc.getMonth()];
              parts.push(monthLongC);
              parts.push(monthShortC);
              parts.push(String(dtc.getFullYear()));
            }
          }
        } catch (e) {}
      }
    }
    if (item.questionnaireAssignments) {
      for (const q of item.questionnaireAssignments) {
        // common shapes: q may directly contain questionnaire info or wrap it in questionnaireAssignment
        if (q.name) parts.push(q.name);
        if (q.title) parts.push(q.title);
        if (q.questionnaire && q.questionnaire.name)
          parts.push(q.questionnaire.name);
        if (
          q.questionnaireAssignment &&
          q.questionnaireAssignment.questionnaire &&
          q.questionnaireAssignment.questionnaire.name
        )
          parts.push(q.questionnaireAssignment.questionnaire.name);
        if (
          q.questionnaireAssignment &&
          q.questionnaireAssignment.questionnaire &&
          q.questionnaireAssignment.questionnaire.type
        )
          parts.push(q.questionnaireAssignment.questionnaire.type);
      }
    }

    return parts.join(" ").toLowerCase();
  };

  // build grouped items from history when it changes; precompute searchable text per item
  useEffect(() => {
    if (!history) return;
    var DateItems = [];
    for (const i of history) {
      const copy = { ...i, _searchable: itemToSearchableText(i) };
      var _entry = DateItems.find(
        (d) => d.triggeredDateTime === copy.triggeredDateTime.substring(0, 10),
      );
      if (_entry) {
        _entry.items.push(copy);
      } else {
        DateItems.push({
          triggeredDateTime: copy.triggeredDateTime.substring(0, 10),
          items: [copy],
        });
      }
    }
    setItems(DateItems);
  }, [history]);

  // scroll to the requested date group once it has rendered
  useEffect(() => {
    if (!scrollToDate || items.length === 0) return;
    const el = document.getElementById(`patient-history-date-${scrollToDate}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollToDate, items]);

  // compute filtered groups while preserving grouping by date
  const filteredGroups = useMemo(() => {
    if (!query || query.trim() === "") return items;
    const tokens = query
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    if (tokens.length === 0) return items;
    const matches = (item) => {
      const text = item._searchable || "";
      return tokens.every((t) => text.indexOf(t) !== -1);
    };
    return items
      .map((g) => ({ ...g, items: g.items.filter((it) => matches(it)) }))
      .filter((g) => g.items && g.items.length > 0);
  }, [items, query]);

  return (
    <div>
      <div className="mb-2">
        <input
          className="form-control"
          placeholder="Search patient history"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div>
          <button onClick={() => setShowModal("note")} className="btn">
            Add a note
          </button>
          <button onClick={() => setShowModal("doc")} className="btn">
            Upload document
          </button>
        </div>
      </div>
      <WorkFlowPatientHistoryAddItemModal
        show={showModal !== undefined}
        setShowModal={setShowModal}
        type={showModal}
      />
      {/* render grouped by date but only groups that have matches when a query is present */}
      {filteredGroups.map((i) => (
        <DateTimeContainer key={i.triggeredDateTime} date={i.triggeredDateTime}>
          {i.items.map((c, idx) => (
            <NotesProvider
              key={c.id || `${i.triggeredDateTime}-${idx}`}
              event={c}
            >
              <WorkFlowHistoryEntryLineItem item={c} />
            </NotesProvider>
          ))}
        </DateTimeContainer>
      ))}
    </div>
  );
}
